import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { Project, Task, Subtask, Discussion, DiscussionMessage, DiscussionTopic, TaskStatus, Role, SubSubtask, TimeLog } from '../types';
import { generateMockProject } from '../utils/mockData';

interface ProjectStore {
  project: Project | null;
  projects: Project[];
  
  // 專案相關操作
  setProject: (project: Project) => void;
  updateProjectProgress: () => void;
  updateTaskProgress: (taskId: string) => void;
  
  // 任務相關操作
  addTask: (task: Task) => void;
  updateTask: (taskId: string, updates: Partial<Task>) => void;
  deleteTask: (taskId: string) => void;
  toggleTaskCollapse: (taskId: string) => void;
  toggleAllTasks: (collapsed: boolean) => void;
  getTaskById: (taskId: string) => Task | undefined;
  addRoleToTask: (taskId: string, role: Role) => void;
  removeRoleFromTask: (taskId: string, role: Role) => void;
  
  // 子任務相關操作
  addSubtask: (taskId: string, subtask: Omit<Subtask, 'id'>) => void;
  updateSubtask: (taskId: string, subtaskId: string, updates: Partial<Subtask>) => void;
  deleteSubtask: (taskId: string, subtaskId: string) => void;
  updateSubtaskStatus: (taskId: string, subtaskId: string, status: TaskStatus) => void;
  
  // 子子任務相關操作
  addSubSubtask: (taskId: string, subtaskId: string, text: string) => void;
  toggleSubSubtask: (taskId: string, subtaskId: string, subSubtaskId: string) => void;
  deleteSubSubtask: (taskId: string, subtaskId: string, subSubtaskId: string) => void;
  
  // 討論相關操作
  discussions: Record<string, Discussion>;
  discussionTopics: Record<string, DiscussionTopic[]>;
  getDiscussion: (taskId: string, subtaskId?: string) => Discussion | undefined;
  getDiscussionTopics: (taskId: string, subtaskId?: string) => DiscussionTopic[];
  addDiscussionMessage: (taskId: string, subtaskId: string | undefined, topic: string, message: Omit<DiscussionMessage, 'id' | 'timestamp'>) => void;
  addDiscussionTopic: (taskId: string, subtaskId: string | undefined, topic: Omit<DiscussionTopic, 'id' | 'messageCount'>) => void;
  replyToMessage: (taskId: string, subtaskId: string | undefined, topic: string, messageId: string, reply: Omit<DiscussionMessage, 'id' | 'timestamp'>) => void;
  getDiscussionCount: (taskId: string, subtaskId?: string) => number;
  
  // UI 狀態
  layoutMode: 'grid' | 'masonry';
  setLayoutMode: (mode: 'grid' | 'masonry') => void;
  
  // 工時記錄相關方法
  addTimeLog: (taskId: string, subtaskId: string, subSubtaskId: string | null, timeLog: Omit<TimeLog, 'id' | 'createdAt'>) => void;
  updateTimeLog: (taskId: string, subtaskId: string, subSubtaskId: string | null, timeLogId: string, updates: Partial<TimeLog>) => void;
  deleteTimeLog: (taskId: string, subtaskId: string, subSubtaskId: string | null, timeLogId: string) => void;
  getTimeLogsByAssignee: (assignee: string) => TimeLog[];
  getTimeLogsByDateRange: (startDate: Date, endDate: Date) => TimeLog[];
}

export const useProjectStore = create<ProjectStore>()(
  persist(
    (set, get) => ({
      project: null,
      projects: [],
      discussions: {},
      discussionTopics: {},
      layoutMode: 'grid',
      
      setProject: (project) => {
        set({ project });
        // Also update projects array
        set((state) => ({
          projects: state.projects.length > 0 
            ? state.projects.map(p => p.id === project.id ? project : p)
            : [project]
        }));
      },
      
      updateProjectProgress: () => set((state) => {
        if (!state.project) return state;
        
        const allSubtasks = state.project.tasks.flatMap(task => task.subtasks);
        const completedSubtasks = allSubtasks.filter(subtask => subtask.status === 'completed');
        const progress = allSubtasks.length > 0 
          ? Math.round((completedSubtasks.length / allSubtasks.length) * 100)
          : 0;
        
        const updatedProject = {
          ...state.project,
          progress
        };
        
        return {
          project: updatedProject,
          projects: state.projects.map(p => p.id === updatedProject.id ? updatedProject : p)
        };
      }),
      
      updateTaskProgress: (taskId) => set((state) => {
        if (!state.project) return state;
        
        const task = state.project.tasks.find(t => t.id === taskId);
        if (!task) return state;
        
        const completedSubtasks = task.subtasks.filter(st => st.status === 'completed').length;
        const totalSubtasks = task.subtasks.length;
        const progress = totalSubtasks > 0 ? Math.round((completedSubtasks / totalSubtasks) * 100) : 0;
        
        const updatedProject = {
          ...state.project,
          tasks: state.project.tasks.map(t => 
            t.id === taskId ? { ...t, progress } : t
          )
        };
        
        return {
          project: updatedProject,
          projects: state.projects.map(p => p.id === updatedProject.id ? updatedProject : p)
        };
      }),
      
      addTask: (task) => set((state) => {
        if (!state.project) return state;
        
        const updatedProject = {
          ...state.project,
          tasks: [...state.project.tasks, task]
        };
        
        return {
          project: updatedProject,
          projects: state.projects.map(p => p.id === updatedProject.id ? updatedProject : p)
        };
      }),
      
      updateTask: (taskId, updates) => set((state) => {
        if (!state.project) return state;
        
        const updatedProject = {
          ...state.project,
          tasks: state.project.tasks.map(task =>
            task.id === taskId ? { ...task, ...updates } : task
          )
        };
        
        return {
          project: updatedProject,
          projects: state.projects.map(p => p.id === updatedProject.id ? updatedProject : p)
        };
      }),
      
      deleteTask: (taskId) => set((state) => {
        if (!state.project) return state;
        
        const updatedProject = {
          ...state.project,
          tasks: state.project.tasks.filter(task => task.id !== taskId)
        };
        
        return {
          project: updatedProject,
          projects: state.projects.map(p => p.id === updatedProject.id ? updatedProject : p)
        };
      }),
      
      toggleTaskCollapse: (taskId) => set((state) => {
        if (!state.project) return state;
        
        const updatedProject = {
          ...state.project,
          tasks: state.project.tasks.map(task =>
            task.id === taskId ? { ...task, isCollapsed: !task.isCollapsed } : task
          )
        };
        
        return {
          project: updatedProject,
          projects: state.projects.map(p => p.id === updatedProject.id ? updatedProject : p)
        };
      }),
      
      toggleAllTasks: (collapsed) => set((state) => {
        if (!state.project) return state;
        
        const updatedProject = {
          ...state.project,
          tasks: state.project.tasks.map(task => ({ ...task, isCollapsed: collapsed }))
        };
        
        return {
          project: updatedProject,
          projects: state.projects.map(p => p.id === updatedProject.id ? updatedProject : p)
        };
      }),
      
      getTaskById: (taskId) => {
        const state = get();
        if (state.project) {
          const task = state.project.tasks.find(t => t.id === taskId);
          if (task) return task;
        }
        for (const project of state.projects) {
          const task = project.tasks.find(t => t.id === taskId);
          if (task) return task;
        }
        return undefined;
      },
      
      addRoleToTask: (taskId, role) => set((state) => {
        if (!state.project) return state;
        
        const updatedProject = {
          ...state.project,
          tasks: state.project.tasks.map(task =>
            task.id === taskId && !task.roles.includes(role)
              ? { ...task, roles: [...task.roles, role] }
              : task
          )
        };
        
        return {
          project: updatedProject,
          projects: state.projects.map(p => p.id === updatedProject.id ? updatedProject : p)
        };
      }),
      
      removeRoleFromTask: (taskId, role) => set((state) => {
        if (!state.project) return state;
        
        const updatedProject = {
          ...state.project,
          tasks: state.project.tasks.map(task =>
            task.id === taskId
              ? { 
                  ...task, 
                  roles: task.roles.filter(r => r !== role),
                  subtasks: task.subtasks.filter(st => st.role !== role)
                }
              : task
          )
        };
        
        return {
          project: updatedProject,
          projects: state.projects.map(p => p.id === updatedProject.id ? updatedProject : p)
        };
      }),
      
      addSubtask: (taskId, subtask) => {
        const newSubtask: Subtask = {
          ...subtask,
          id: `subtask-${Date.now()}`
        };
        
        set((state) => {
          if (!state.project) return state;
          
          const updatedProject = {
            ...state.project,
            tasks: state.project.tasks.map(task =>
              task.id === taskId 
                ? { ...task, subtasks: [...task.subtasks, newSubtask] }
                : task
            )
          };
          
          return {
            project: updatedProject,
            projects: state.projects.map(p => p.id === updatedProject.id ? updatedProject : p)
          };
        });
        
        // Update progress
        get().updateTaskProgress(taskId);
        get().updateProjectProgress();
      },
      
      updateSubtask: (taskId, subtaskId, updates) => set((state) => {
        if (!state.project) return state;
        
        const updatedProject = {
          ...state.project,
          tasks: state.project.tasks.map(task =>
            task.id === taskId 
              ? {
                  ...task,
                  subtasks: task.subtasks.map(subtask =>
                    subtask.id === subtaskId 
                      ? { ...subtask, ...updates }
                      : subtask
                  )
                }
              : task
          )
        };
        
        return {
          project: updatedProject,
          projects: state.projects.map(p => p.id === updatedProject.id ? updatedProject : p)
        };
      }),
      
      deleteSubtask: (taskId, subtaskId) => {
        set((state) => {
          if (!state.project) return state;
          
          const updatedProject = {
            ...state.project,
            tasks: state.project.tasks.map(task =>
              task.id === taskId 
                ? {
                    ...task,
                    subtasks: task.subtasks.filter(subtask => subtask.id !== subtaskId)
                  }
                : task
            )
          };
          
          return {
            project: updatedProject,
            projects: state.projects.map(p => p.id === updatedProject.id ? updatedProject : p)
          };
        });
        
        // Update progress
        get().updateTaskProgress(taskId);
        get().updateProjectProgress();
      },
      
      updateSubtaskStatus: (taskId, subtaskId, status) => {
        set((state) => {
          if (!state.project) return state;
          
          const updatedProject = {
            ...state.project,
            tasks: state.project.tasks.map(task =>
              task.id === taskId 
                ? {
                    ...task,
                    subtasks: task.subtasks.map(subtask =>
                      subtask.id === subtaskId 
                        ? { 
                            ...subtask, 
                            status,
                            completedDate: status === 'completed' ? new Date() : undefined
                          }
                        : subtask
                    )
                  }
                : task
            )
          };
          
          return {
            project: updatedProject,
            projects: state.projects.map(p => p.id === updatedProject.id ? updatedProject : p)
          };
        });
        
        // Update progress
        get().updateTaskProgress(taskId);
        get().updateProjectProgress();
      },
      
      addSubSubtask: (taskId, subtaskId, text) => set((state) => {
        if (!state.project) return state;
        
        const newSubSubtask: SubSubtask = {
          id: Date.now().toString(),
          text,
          completed: false,
          timeLogs: [],
        };
        
        const updatedProject = {
          ...state.project,
          tasks: state.project.tasks.map(task =>
            task.id === taskId 
              ? {
                  ...task,
                  subtasks: task.subtasks.map(subtask =>
                    subtask.id === subtaskId 
                      ? {
                          ...subtask,
                          subSubtasks: [...(subtask.subSubtasks || []), newSubSubtask]
                        }
                      : subtask
                  )
                }
              : task
          )
        };
        
        return {
          project: updatedProject,
          projects: state.projects.map(p => p.id === updatedProject.id ? updatedProject : p)
        };
      }),
      
      toggleSubSubtask: (taskId, subtaskId, subSubtaskId) => {
        set((state) => {
          if (!state.project) return state;
          
          const updatedProject = {
            ...state.project,
            tasks: state.project.tasks.map(task =>
              task.id === taskId 
                ? {
                    ...task,
                    subtasks: task.subtasks.map(subtask =>
                      subtask.id === subtaskId 
                        ? {
                            ...subtask,
                            subSubtasks: subtask.subSubtasks?.map(sst =>
                              sst.id === subSubtaskId 
                                ? { ...sst, completed: !sst.completed }
                                : sst
                            )
                          }
                        : subtask
                    )
                  }
                : task
            )
          };
          
          return {
            project: updatedProject,
            projects: state.projects.map(p => p.id === updatedProject.id ? updatedProject : p)
          };
        });
        
        // Update progress
        get().updateTaskProgress(taskId);
        get().updateProjectProgress();
      },
      
      deleteSubSubtask: (taskId, subtaskId, subSubtaskId) => {
        set((state) => {
          if (!state.project) return state;
          
          const updatedProject = {
            ...state.project,
            tasks: state.project.tasks.map(task =>
              task.id === taskId 
                ? {
                    ...task,
                    subtasks: task.subtasks.map(subtask =>
                      subtask.id === subtaskId 
                        ? {
                            ...subtask,
                            subSubtasks: subtask.subSubtasks?.filter(sst => sst.id !== subSubtaskId) || []
                          }
                        : subtask
                    )
                  }
                : task
            )
          };
          
          return {
            project: updatedProject,
            projects: state.projects.map(p => p.id === updatedProject.id ? updatedProject : p)
          };
        });
        
        // Update progress
        get().updateTaskProgress(taskId);
        get().updateProjectProgress();
      },
      
      getDiscussion: (taskId, subtaskId) => {
        const key = subtaskId ? `${taskId}-${subtaskId}` : taskId;
        return get().discussions[key];
      },
      
      getDiscussionTopics: (taskId, subtaskId) => {
        const key = subtaskId ? `${taskId}-${subtaskId}` : taskId;
        const defaultTopics: DiscussionTopic[] = [
          { id: 'general', name: '一般討論', icon: '📋', messageCount: 0 },
          { id: 'technical', name: '技術問題', icon: '🔧', messageCount: 0 },
          { id: 'design', name: '設計相關', icon: '🎨', messageCount: 0 },
          { id: 'testing', name: '測試回饋', icon: '🧪', messageCount: 0 }
        ];
        
        const customTopics = get().discussionTopics[key] || [];
        const discussion = get().discussions[key];
        
        // Update message counts
        const allTopics = [...defaultTopics, ...customTopics];
        if (discussion && discussion.topics) {
          allTopics.forEach(topic => {
            topic.messageCount = discussion.topics[topic.id]?.length || 0;
          });
        }
        
        return allTopics;
      },
      
      addDiscussionMessage: (taskId, subtaskId, topic, message) => {
        const key = subtaskId ? `${taskId}-${subtaskId}` : taskId;
        const newMessage: DiscussionMessage = {
          ...message,
          id: Date.now().toString(),
          timestamp: new Date()
        };
        
        set((state) => {
          const discussion = state.discussions[key] || {
            taskId,
            subtaskId,
            topics: {},
            activeTopics: []
          };
          
          // Update discussions
          const newState = {
            discussions: {
              ...state.discussions,
              [key]: {
                ...discussion,
                topics: {
                  ...discussion.topics,
                  [topic]: [...(discussion.topics[topic] || []), newMessage]
                }
              }
            }
          };
          
          // Also update discussion count in task/subtask
          if (state.project) {
            const updatedProject = {
              ...state.project,
              tasks: state.project.tasks.map(task => {
                if (task.id === taskId) {
                  if (subtaskId) {
                    // Update subtask discussion count
                    return {
                      ...task,
                      subtasks: task.subtasks.map(st => 
                        st.id === subtaskId 
                          ? { ...st, discussionCount: st.discussionCount + 1 }
                          : st
                      )
                    };
                  } else {
                    // Update task discussion count
                    return { ...task, discussionCount: task.discussionCount + 1 };
                  }
                }
                return task;
              })
            };
            
            return {
              ...newState,
              project: updatedProject,
              projects: state.projects.map(p => p.id === updatedProject.id ? updatedProject : p)
            };
          }
          
          return newState;
        });
      },
      
      addDiscussionTopic: (taskId, subtaskId, topic) => {
        const key = subtaskId ? `${taskId}-${subtaskId}` : taskId;
        const newTopic: DiscussionTopic = {
          ...topic,
          id: Date.now().toString(),
          messageCount: 0
        };
        
        set((state) => ({
          discussionTopics: {
            ...state.discussionTopics,
            [key]: [...(state.discussionTopics[key] || []), newTopic]
          }
        }));
      },
      
      replyToMessage: (taskId, subtaskId, topic, messageId, reply) => {
        const key = subtaskId ? `${taskId}-${subtaskId}` : taskId;
        const newReply: DiscussionMessage = {
          ...reply,
          id: Date.now().toString(),
          timestamp: new Date()
        };
        
        set((state) => {
          const discussion = state.discussions[key];
          if (!discussion) return state;
          
          const updateMessages = (messages: DiscussionMessage[]): DiscussionMessage[] => {
            return messages.map(msg => {
              if (msg.id === messageId) {
                return {
                  ...msg,
                  replies: [...(msg.replies || []), newReply]
                };
              } else if (msg.replies) {
                return {
                  ...msg,
                  replies: updateMessages(msg.replies)
                };
              }
              return msg;
            });
          };
          
          return {
            discussions: {
              ...state.discussions,
              [key]: {
                ...discussion,
                topics: {
                  ...discussion.topics,
                  [topic]: updateMessages(discussion.topics[topic] || [])
                }
              }
            }
          };
        });
      },
      
      getDiscussionCount: (taskId, subtaskId) => {
        const key = subtaskId ? `${taskId}-${subtaskId}` : taskId;
        const discussion = get().discussions[key];
        if (!discussion || !discussion.topics) return 0;
        
        let count = 0;
        Object.values(discussion.topics).forEach((messages) => {
          count += messages.length;
          // Count replies too
          messages.forEach(msg => {
            if (msg.replies) {
              count += msg.replies.length;
            }
          });
        });
        
        return count;
      },
      
      setLayoutMode: (mode) => set({ layoutMode: mode }),
      
      // 工時記錄相關方法實現
      addTimeLog: (taskId, subtaskId, subSubtaskId, timeLogData) => {
        set((state) => {
          if (!state.project) return state;
          
          const newTimeLog: TimeLog = {
            ...timeLogData,
            id: `timelog-${Date.now()}`,
            createdAt: new Date(),
          };
          
          return {
            project: {
              ...state.project,
              tasks: state.project.tasks.map((task) =>
                task.id === taskId
                  ? {
                      ...task,
                      subtasks: task.subtasks.map((subtask) =>
                        subtask.id === subtaskId
                          ? subSubtaskId
                            ? {
                                ...subtask,
                                subSubtasks: subtask.subSubtasks.map((subSubtask) =>
                                  subSubtask.id === subSubtaskId
                                    ? {
                                        ...subSubtask,
                                        timeLogs: [...subSubtask.timeLogs, newTimeLog],
                                      }
                                    : subSubtask
                                ),
                              }
                            : {
                                ...subtask,
                                timeLogs: [...subtask.timeLogs, newTimeLog],
                              }
                          : subtask
                      ),
                    }
                  : task
              ),
            },
          };
        });
      },

      updateTimeLog: (taskId, subtaskId, subSubtaskId, timeLogId, updates) => {
        set((state) => {
          if (!state.project) return state;
          
          return {
            project: {
              ...state.project,
              tasks: state.project.tasks.map((task) =>
                task.id === taskId
                  ? {
                      ...task,
                      subtasks: task.subtasks.map((subtask) =>
                        subtask.id === subtaskId
                          ? subSubtaskId
                            ? {
                                ...subtask,
                                subSubtasks: subtask.subSubtasks.map((subSubtask) =>
                                  subSubtask.id === subSubtaskId
                                    ? {
                                        ...subSubtask,
                                        timeLogs: subSubtask.timeLogs.map((log) =>
                                          log.id === timeLogId
                                            ? { ...log, ...updates }
                                            : log
                                        ),
                                      }
                                    : subSubtask
                                ),
                              }
                            : {
                                ...subtask,
                                timeLogs: subtask.timeLogs.map((log) =>
                                  log.id === timeLogId
                                    ? { ...log, ...updates }
                                    : log
                                ),
                              }
                          : subtask
                      ),
                    }
                  : task
              ),
            },
          };
        });
      },

      deleteTimeLog: (taskId, subtaskId, subSubtaskId, timeLogId) => {
        set((state) => {
          if (!state.project) return state;
          
          return {
            project: {
              ...state.project,
              tasks: state.project.tasks.map((task) =>
                task.id === taskId
                  ? {
                      ...task,
                      subtasks: task.subtasks.map((subtask) =>
                        subtask.id === subtaskId
                          ? subSubtaskId
                            ? {
                                ...subtask,
                                subSubtasks: subtask.subSubtasks.map((subSubtask) =>
                                  subSubtask.id === subSubtaskId
                                    ? {
                                        ...subSubtask,
                                        timeLogs: subSubtask.timeLogs.filter(
                                          (log) => log.id !== timeLogId
                                        ),
                                      }
                                    : subSubtask
                                ),
                              }
                            : {
                                ...subtask,
                                timeLogs: subtask.timeLogs.filter(
                                  (log) => log.id !== timeLogId
                                ),
                              }
                          : subtask
                      ),
                    }
                  : task
              ),
            },
          };
        });
      },

      getTimeLogsByAssignee: (assignee) => {
        const state = get();
        if (!state.project) return [];
        
        const allTimeLogs: TimeLog[] = [];
        
        state.project.tasks.forEach((task) => {
          task.subtasks.forEach((subtask) => {
            // 子任務的工時記錄
            allTimeLogs.push(...subtask.timeLogs.filter((log) => log.assignee === assignee));
            
            // 細項的工時記錄
            subtask.subSubtasks.forEach((subSubtask) => {
              allTimeLogs.push(...subSubtask.timeLogs.filter((log) => log.assignee === assignee));
            });
          });
        });
        
        return allTimeLogs;
      },

      getTimeLogsByDateRange: (startDate, endDate) => {
        const state = get();
        if (!state.project) return [];
        
        const allTimeLogs: TimeLog[] = [];
        
        state.project.tasks.forEach((task) => {
          task.subtasks.forEach((subtask) => {
            // 子任務的工時記錄
            allTimeLogs.push(
              ...subtask.timeLogs.filter(
                (log) => log.date >= startDate && log.date <= endDate
              )
            );
            
            // 細項的工時記錄
            subtask.subSubtasks.forEach((subSubtask) => {
              allTimeLogs.push(
                ...subSubtask.timeLogs.filter(
                  (log) => log.date >= startDate && log.date <= endDate
                )
              );
            });
          });
        });
        
        return allTimeLogs;
      },
    }),
    {
      name: 'project-storage',
      storage: createJSONStorage(() => localStorage, {
        reviver: (key, value) => {
          if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value)) {
            return new Date(value);
          }
          return value;
        },
        replacer: (key, value) => {
          if (value instanceof Date) {
            return value.toISOString();
          }
          return value;
        },
      }),
    }
  )
); 